import { refreshTokenAPI, requestApiGet, requestApiPostTwo, requestAuthApiV2, setAuthTokenInHeader } from '../api';
import { isAuthV2Enabled } from '../utils/authMode';
import { dispatchToken, logout, useTokenStore } from '../storage/tokenState';
import { isEmpty } from "lodash/";
import { AUTHENTICATE_USER, REFRESH_TOKEN, USER_DETAILS } from '../appConstants/APIEndpoint';
import { ELanguage, IUserDetails, PreSignUpConfig } from '../storage/store';
import { dispatchPostSignupConfig, dispatchPreSignupConfig, dispatchSignUpData } from '../storage/configState';
import { type AuthApiOne } from '../interfaces/AuthApi';
import { MAIN_CLIENT, SIGN_IN_ACTION, GOOGLE_LOGIN_KEY } from '../appConstants/AppConstant';
import { dispatchUserDetails } from '../storage/userState';
import { DIALING_CODE } from '../constant';
import { v4 as uuidv4 } from 'uuid';
import { _isEmpty } from '../utils/helpers/zupeeLodash';
import { initializeGameplaySocket } from '../classes/socket/gameplay';
import { RJT, SIGN_IN_CODES } from '../utils/helpers/constants';
import { getDeviceId } from '../utils/helpers/deviceId';
import { OnlyPostSignUpConfig } from '../interfaces/PrivateConfig';
import { splitSwitchesFromPostSignUp, splitSwitchesFromPreSignUp } from '../utils/beToFeTransformer';
import { dispatchSwitchData } from '../storage/switchState';
import { getNetworkInfo } from '../utils/NetInfo';
import { getDeviceSpec, getFormattedTimestamp } from '../utils/helpers/common';
import { getOSVersion } from '../utils/helpers/common';
import { setAuthApiResponseStatus } from '../store/actions/actions';

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (config: any) => void;
          prompt: (callback: (notification: any) => void) => void;
        };
        oauth2?: {
          initTokenClient: (config: any) => any;
        };
      };
    };
  }
}

declare const google: any;

let isRefreshingToken = false;

const getAuthApiRequestParamsV1 = (
  phone: string,
  action: string,
  language: string,
  isNewAuth: boolean, // true for new signup, false for login
  otp?: string,
  accessToken?: string,
  refreshToken?: string,
  _id?: string,
  zupeeUserId?: number,
  refreshTokenExpiresAt?: number,
  partnerId?: string,
  screenName?: string,
  tokenForLoginSkip?: string
) => {
  const deviceID = getDeviceId();
  const networkInfo = getNetworkInfo();
  const { isConnected, effectiveType } = networkInfo;
  const osVersion = getOSVersion();
  const deviceSpec = getDeviceSpec();
  const formattedTimestamp = getFormattedTimestamp();
  // const deviceID = DeviceInfo.getUniqueId();

  // const appVersion = Platform.OS === 'android' ? '3000' : '656';

  // Common parameters for both signup and login
  const baseParams = {
    socketType: MAIN_CLIENT,
    merchantName: "SUPER_ZUPEE",
    DeviceId: deviceID,
    det: 'android',
    signupPhoneNumber: phone,
    gaId: deviceID,
    ult: 'phone',
    lc: localStorage.getItem("language") || "en",
    languagePreference: localStorage.getItem("language") || "en",
    isNewSignupFlowEnabled: true,
    av: `${process.env.REACT_APP_VERSION}`,
    version_code: `${process.env.REACT_APP_VERSION}`,
    app_version: `${process.env.REACT_APP_APPVERSION}`,
    anov: osVersion,
    rfc: '',
    uniqueDeviceId: deviceID,
    packageName: `${process.env.REACT_APP_APP_NAME}`,
    SerialNumber: deviceID,
    pwaAuthData: {
      partnerId: partnerId,
      userAuthenticatedScreen: screenName || ''
   },
   connectionInfo: {
    isConnected: isConnected,
    networkType: effectiveType
  },
  deviceType: "android",
  specs: deviceSpec,
  // isUsbDebuggingEnabled: true,
  timeStamp: formattedTimestamp,
  "user-property": "00000",
  tokenForLoginSkip: tokenForLoginSkip
  };

  if (isNewAuth) {
    return {
      ...baseParams,
      newauth: true,
      otp: otp,
      action: action,
    };
  } else {
    return {
      ...baseParams,
      reauth: true,
      lastAccessToken: accessToken,
      refreshToken: refreshToken || '',
      uid: _id,
      zupeeUserId: zupeeUserId,
      refreshTokenExpiresAt,
    };
  }
};

/**
 * v2 params, matching verticaltv-pwa's authenticateUser payload exactly
 * (debug.log:245-262, which succeeds where v1 returns 1013).
 *
 * Deliberately omits `pwaAuthData` and `isNewSignupFlowEnabled` — the payload's
 * "this is the PWA" markers — along with the extra device/telemetry fields
 * Vertical does not send.
 *
 * Only the new-auth (OTP) branch is Vertical-shaped. The reauth branch used by
 * silent login delegates to v1: the log gives no Vertical reference for it, and
 * reauth is not what returns 1013, so inventing a shape would risk breaking
 * session restore for no evidenced gain.
 */
const getAuthApiRequestParamsV2 = (
  phone: string,
  action: string,
  language: string,
  isNewAuth: boolean,
  otp?: string,
  accessToken?: string,
  refreshToken?: string,
  _id?: string,
  zupeeUserId?: number,
  refreshTokenExpiresAt?: number,
  partnerId?: string,
  screenName?: string,
  tokenForLoginSkip?: string
) => {
  if (!isNewAuth) {
    return getAuthApiRequestParamsV1(
      phone, action, language, isNewAuth, otp, accessToken, refreshToken,
      _id, zupeeUserId, refreshTokenExpiresAt, partnerId, screenName,
      tokenForLoginSkip,
    );
  }

  const deviceID = getDeviceId();
  const osVersion = getOSVersion();

  return {
    ult: 'phone',
    newauth: true,
    signupPhoneNumber: phone,
    otp: otp,
    action: action,
    languagePreference: localStorage.getItem('language') || language || 'en',
    merchantName: 'SUPER_ZUPEE',
    // Vertical sends its own build number here; keep this app's.
    av: `${process.env.REACT_APP_VERSION}`,
    deviceType: 'android',
    det: 'android',
    user_login_type: 'android',
    uniqueDeviceId: deviceID,
    gaId: deviceID,
    socketType: MAIN_CLIENT,
    packageName: `${process.env.REACT_APP_APP_NAME}`,
    anov: osVersion,
  };
};

export type TAuthRequestParams =
  | ReturnType<typeof getAuthApiRequestParamsV1>
  | ReturnType<typeof getAuthApiRequestParamsV2>;

/**
 * Picks the request shape. Call sites are unchanged — they keep calling this.
 */
export const getAuthApiRequestParams = (
  ...args: Parameters<typeof getAuthApiRequestParamsV1>
): TAuthRequestParams =>
  isAuthV2Enabled()
    ? getAuthApiRequestParamsV2(...args)
    : getAuthApiRequestParamsV1(...args);

export const authenticateUserApi = async (
  request: TAuthRequestParams,
  dispatch?: any
) => {
  try {
    const updatedRequest = { ...request };
    const lastAccessToken = 'reauth' in request ? request.lastAccessToken : undefined;
    // v2 posts with clean headers so the PWA markers are not sent; v1 keeps the
    // shared axios instance and all of its headers.
    const postAuth = isAuthV2Enabled() ? requestAuthApiV2 : requestApiPostTwo;
    const responseData = await postAuth<AuthApiOne>(
      AUTHENTICATE_USER,
      updatedRequest
    );
    if (responseData?.response?.success) {
      const authResponse = responseData.response;
      if (authResponse?.flag === false && authResponse?.errorCode !== SIGN_IN_CODES.SUCCESS_CODE) {
        if (authResponse?.errorCode === SIGN_IN_CODES.INCORRECT_OTP) {
          console.log('Silent Login failed due to incorrect OTP', { isAddEvent: true });
          // logout();
          return responseData.response;
        }
        return responseData.response;
      }
      saveAuthApiResponse(authResponse, lastAccessToken);
      dispatch && dispatch(setAuthApiResponseStatus(true))
      return responseData.response;
    } else {
      console.log("Auth API failed");
    }
  } catch (error) {
    console.error("Network or Logic Error:", error);
    // Only throw if `error` is not already an instance of Error
    // throw error instanceof Error ? error : new Error(`Auth API failed: ${error}`);
  }
};

export const silentLoginByAuthAPI = async (dispatch?: any) => {
  const token = useTokenStore.getState().token;
  // logger(TAG_NAMES.NAVIGATOR, 'Silent Login Triggered');
  if (!token) {
    // logger(TAG_NAMES.API_LOGS, 'Unable to login user since no token');
    return;
  }
  const {
    accessToken,
    refreshToken,
    _id,
    zupeeUserId,
    signupPhoneNumber,
    refreshTokenExpiresAt,
    language=localStorage.getItem('language') || 'en',
  } = token;
  if (!signupPhoneNumber) {
    // logger(
    //   TAG_NAMES.API_LOGS,
    //   'Unable to login user since no signupPhoneNumber',
    // );
    return;
  }
  const requestParam = getAuthApiRequestParams(
    signupPhoneNumber,
    SIGN_IN_ACTION,
    language,
    false,
    undefined,
    accessToken,
    refreshToken,
    _id,
    zupeeUserId,
    refreshTokenExpiresAt,
  );
  authenticateUserApi(requestParam, dispatch).then(async authResponse => {
    authErrorHandler(authResponse)

    await fetchAndStoreUserDetails(language as ELanguage);
    if(authResponse?.rejoinInfo?.rejoin === 1){
      if (!_isEmpty(authResponse?.rejoinInfo?.gameplayServer)) {
        // console.log(" gameplayServergameplayServer", gameplayServer)
        initializeGameplaySocket(authResponse?.rejoinInfo?.gameplayServer, {
          en: RJT,
          data: { ltid: "000" },
        });
      }
    }
  }).catch(error => {
    logout();
    console.log('Silent Login Failed', {
      isAddEvent: true,
      error,
    });
  });;
};

export const saveAuthApiResponse = (authResponse: any, lastAccessToken?: string) => {
  const signUpData = authResponse?.signUpData;
  const postConfig = authResponse?.postConfig;
  const token = useTokenStore.getState().token;
  const {
    pp,
    ue,
    un,
    ult,
    signupPhoneNumber,
    _id,
    accessToken,
    zupeeUserId,
    refreshToken,
    refreshTokenExpiresAt,
    validity,
    isNewSignup,
    rummyAccessToken,
  } = signUpData || {};

  const dataToSave = {
    pp,
    un,
    ue,
    ult,
    accessToken,
    refreshToken: refreshToken ? refreshToken : token.refreshToken,
    signupPhoneNumber,
    _id,
    zupeeUserId,
    refreshTokenExpiresAt,
    validity: Date.now() + validity - 10, //We're adding 10 seconds to avoid refreshing token at the last second
    isNewSignup,
    rummyAccessToken,
    lastAccessToken: lastAccessToken || "",
  };
  dispatchToken(dataToSave);

  if (accessToken && _id) {
    accessToken && _id && setAuthTokenInHeader(accessToken, _id);
    // logger(TAG_NAMES.API_LOGS, 'Token Saved', { data: { accessToken, _id } });
  }

  if (postConfig) {
    // logger(TAG_NAMES.API_LOGS, 'Post Signup saved');
    const { _otherData, _switchPostSignUpData } = splitSwitchesFromPostSignUp(postConfig);
    dispatchPostSignupConfig(_otherData as OnlyPostSignUpConfig);
    dispatchSwitchData(_switchPostSignUpData);
  }

  if (signUpData) {
    // logger(TAG_NAMES.API_LOGS, 'Signup data saved');
    dispatchSignUpData(signUpData);
  }

  // fetchOnboardingConfig(
  //   signUpData?.onboardingCompleted,
  //   authResponse?.rejoinInfo?.rejoin || 0,
  // ); //Call once Header is set

  //TODO Test this API with ScreenName useGetNudgesByScreenNameAPI('LOBBY');
};

/**
 * Refreshes user details into the store.
 *
 * Returns whether the store actually changed. `requestApiGet` swallows transport
 * errors into `{ success: false }`, so a failure here is silent — callers that
 * gate navigation on fresh state (the KYC gate) need to be able to tell the
 * difference rather than acting on stale values.
 */
export const fetchAndStoreUserDetails = async (
  language: ELanguage,
): Promise<boolean> => {
  const userResp = await requestApiGet<IUserDetails>(USER_DETAILS);
  if (isEmpty(userResp.response.userDetail)) { return false; }

  dispatchUserDetails(userResp.response.userDetail, language);
  return true;
};

export const refreshTokenDuplicate = async () => {
  try {
    const token = useTokenStore.getState().token;
    const { ult, accessToken, refreshToken, refreshTokenExpiresAt = 0 } = token || {};
    console.log('refreshTokenDuplicate', token);
    if (!isEmpty(refreshToken) && refreshTokenExpiresAt > Date.now()) {

      // const serialNumber = await getUniqueDeviceId();
      const data = {
        // deviceType: Platform.OS,
        // deviceId: serialNumber,
        loginType: ult,
        refreshToken,
        accessToken,
      };

      console.log('refreshTokenDuplicate API called');
      const res = await refreshTokenAPI(REFRESH_TOKEN, data);
      const { accessToken: newAccessToken, success, validity } = res.response;
      console.log('refreshTokenDuplicate API response', res);
      if (success) {

        const newToken = { ...token, accessToken: newAccessToken, validity: Date.now() + validity - 10 };
        dispatchToken(newToken);
        setAuthTokenInHeader(newAccessToken);

        return Promise.resolve(accessToken);
      } else {
        logout();
        return Promise.reject();
      }
    }

    // No refresh token at all means there is no session to end -- the caller is
    // an anonymous request that 401'd. Logging out here wipes storage and
    // hard-navigates, and the next boot repeats the same request: that is the
    // loop. An *expired* refresh token is a different thing, a session that is
    // genuinely dead, so that still logs out.
    if (isEmpty(refreshToken)) {
      return Promise.reject(new Error('NO_REFRESH_TOKEN'));
    }

    logout();
    return Promise.reject(new Error('REFRESH_TOKEN_EXPIRED'));
  } catch (error: any) {
    // Reached only on a JS error -- refreshTokenAPI swallows transport failures
    // into `{ success: false }`, handled above. Ending a live session over a
    // TypeError is never right.
    return Promise.reject(error);
  }
};

export const isTokenValid = () => {
  const token = useTokenStore.getState()?.token;
  if (isEmpty(token)) {
    return true;
  }
  const accessTokenValidity = token?.validity || 0;
  console.log(`About to refresh after, ${accessTokenValidity - Date.now()}`);

  return !isEmpty(token?.accessToken) && accessTokenValidity > Date.now();
};

export const onPreSignupSuccess = (preSignupConfig: PreSignUpConfig) => {
  if (!isEmpty(preSignupConfig)) {
    const { _otherData, _switchPreSignUpData } = splitSwitchesFromPreSignUp(preSignupConfig);
    dispatchPreSignupConfig(_otherData);
    dispatchSwitchData(_switchPreSignUpData);
  }
};

const authErrorHandler =(authResponse: ({ success: boolean; error?: string; } & Partial<AuthApiOne>) | undefined)=>{
  if (authResponse?.flag === false && authResponse?.errorCode === SIGN_IN_CODES.INVALID_REFRESH_TOKEN) {
    console.log('Silent Login failed due to invalid refresh token');
    logout();
    return;
  }
  if (authResponse?.flag === false && authResponse?.errorCode !== SIGN_IN_CODES.SUCCESS_CODE) {
    if (authResponse?.errorCode === SIGN_IN_CODES.INCORRECT_OTP) {
      console.log('Silent Login failed due to incorrect OTP', { isAddEvent: true });
      // logout();
      return;
    }
    console.log('Silent Login failed due to some error', {
      data: { errorCode: authResponse?.errorCode },
    });
    logout();
    return;
  }
}

/**
 * Silently refreshes the Google token before it expires
 * @returns Promise<string> - Returns the fresh token
 */
export const refreshGoogleToken = async (forceRefresh = false): Promise<string> => {
  try {
    // Prevent concurrent refresh attempts
    if (isRefreshingToken) {
      console.log('Token refresh already in progress, skipping...');
      return Promise.reject('Token refresh already in progress');
    }

    const token = sessionStorage.getItem(GOOGLE_LOGIN_KEY);
    if (!token) {
      return Promise.reject('No Google token found');
    }

    // Decode the existing token to check expiration
    const decodedToken = JSON.parse(atob(token.split('.')[1]));
    const expirationTime = decodedToken.exp * 1000;
    const currentTime = Date.now();
    
    console.log('Token status:', {
      currentTime: new Date(currentTime).toLocaleString(),
      expirationTime: new Date(expirationTime).toLocaleString(),
      timeRemaining: Math.floor((expirationTime - currentTime) / 1000 / 60) + ' minutes'
    });

    // Skip expiration check if forceRefresh is true
    if (!forceRefresh && expirationTime - currentTime > 5 * 60 * 1000) {
      console.log('Token still valid, no refresh needed');
      return token;
    }

    console.log(forceRefresh ? 'Forcing token refresh...' : 'Attempting to refresh token...');
    isRefreshingToken = true;

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        isRefreshingToken = false;
        reject('Token refresh timeout');
      }, 30000); // 30 second timeout

      google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
        callback: (response: any) => {
          clearTimeout(timeoutId);
          isRefreshingToken = false;
          if (response.credential) {
            console.log('New JWT token received:', response);
            sessionStorage.setItem(GOOGLE_LOGIN_KEY, response.credential);
            resolve(response.credential);
          } else {
            console.log('Failed to get JWT token:', response);
            reject('Failed to get JWT token');
          }
        },
        prompt_parent_id: "google_signin_prompt",
        auto_select: true
      });

      try {
        google.accounts.id.prompt((notification: any) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            clearTimeout(timeoutId);
            isRefreshingToken = false;
            console.log('Google prompt not displayed or skipped. Falling back to existing token');
            resolve(token); // Fall back to existing token instead of rejecting
          }
        });
      } catch (error) {
        clearTimeout(timeoutId);
        isRefreshingToken = false;
        reject(error);
      }
    });
  } catch (error) {
    isRefreshingToken = false;
    console.error('Error refreshing Google token:', error);
    return Promise.reject(error);
  }
};

// Helper function to set up periodic token refresh
export const setupGoogleTokenRefresh = (testMode = false) => {
  const refreshInterval = testMode ? 5 * 60 * 1000 : 55 * 60 * 1000;
  console.log(`Setting up token refresh every ${refreshInterval/1000/60} minutes`);
  
  // Initial check with force refresh
  refreshGoogleToken(testMode)
    .then(() => console.log('Initial token check completed'))
    .catch(error => console.error('Initial token check failed:', error));

  return setInterval(async () => {
    console.log('Running scheduled token refresh...');
    try {
      const newToken = await refreshGoogleToken(testMode);
      console.log('Token refresh successful:', newToken.substring(0, 10) + '...');
    } catch (error) {
      console.error('Failed to refresh Google token:', error);
    }
  }, refreshInterval);
};